import * as React from 'react';
import { connect } from 'react-redux';
import { Modal } from 'antd';
import styled from 'styled-components';
import { SwapOutlined } from '@ant-design/icons';

import { AppState } from 'common/appState';
import { redirectToUrl } from 'common/actions/navigationAction';
import { colors } from 'theme/variables';

import { UserOrganisation } from '../models/userModel';
import { OrganisationSelectList } from '../components/OrganisationSelectList';
import {
    toggleUserOrganisationSelectModal,
    updateUserActiveOrganisation,
} from '../actions/userActions';
import { getActiveOrganisation } from '../selectors/userSelectors';

const StyledModal = styled(Modal)`
    &&& .ant-modal-content {
        border-radius: 14px;
        overflow: hidden;
    }
    &&& .ant-modal-header {
        background: ${colors.secondaryColor};
        border-bottom: none;
        padding: 20px 24px;
    }
    &&& .ant-modal-title {
        color: #fff;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 1rem;
        display: flex;
        align-items: center;
        gap: 10px;
    }
    &&& .ant-modal-close-x {
        color: rgba(255, 255, 255, 0.5);
    }
    &&& .ant-modal-body {
        padding: 16px 24px 24px;
    }

    .ant-table-wrapper {
        border: none;
        box-shadow: none;
    }
    .ant-table-thead > tr > th {
        background: #f5f7fa;
        color: ${colors.secondaryColor};
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        font-weight: 600;
        padding: 10px 16px;
    }
    .ant-table-tbody > tr > td {
        padding: 12px 16px;
        font-family: 'Inter', sans-serif;
        font-size: 0.9rem;
    }
    .ant-table-tbody > tr:hover > td {
        background: #f0f7ff;
    }
    .ant-btn-primary {
        border-radius: 6px;
        font-family: 'Inter', sans-serif;
        font-size: 0.82rem;
        height: 34px;
    }
`;

const TitleIcon = styled(SwapOutlined)`
    font-size: 18px;
    color: ${colors.primary};
`;

interface OrganisationSelectModalContainerProps {
    isOrganisationSelectModalOpen: boolean;
    isFetching: boolean;
    organisations: UserOrganisation[];
    activeOrganisation?: UserOrganisation;
    toggleUserOrganisationSelectModal: typeof toggleUserOrganisationSelectModal;
    updateUserActiveOrganisation: typeof updateUserActiveOrganisation;
    redirectToUrl: typeof redirectToUrl;
}

interface OrganisationSelectModalDispatchProps {
    toggleUserOrganisationSelectModal: typeof toggleUserOrganisationSelectModal;
    updateUserActiveOrganisation: typeof updateUserActiveOrganisation;
}

type OrganisationSelectModalProps = OrganisationSelectModalContainerProps &
    OrganisationSelectModalDispatchProps;

class OrganisationSelectModal extends React.Component<OrganisationSelectModalProps> {
    private setActiveOrganisation = (organisation: UserOrganisation) => {
        this.props.toggleUserOrganisationSelectModal(false);
        this.props.updateUserActiveOrganisation(organisation);
        this.props.redirectToUrl('/');
    };

    public render(): JSX.Element {
        const { isOrganisationSelectModalOpen, organisations, isFetching, activeOrganisation } =
            this.props;

        return (
            <StyledModal
                visible={isOrganisationSelectModalOpen}
                closable={Boolean(activeOrganisation)}
                footer={null}
                title={
                    <>
                        <TitleIcon />
                        Select Organisation
                    </>
                }
                onCancel={
                    Boolean(activeOrganisation)
                        ? () => this.props.toggleUserOrganisationSelectModal(false)
                        : undefined
                }
            >
                <OrganisationSelectList
                    organisationsList={organisations}
                    setActiveOrganisation={this.setActiveOrganisation}
                    isFetching={isFetching}
                />
            </StyledModal>
        );
    }
}

function mapStateToProps(state: AppState) {
    return {
        isOrganisationSelectModalOpen: state.user.isOrganisationSelectModalOpen,
        isFetching: state.user.isFetchingOrganisations,
        organisations: state.user.organisations,
        activeOrganisation: getActiveOrganisation(state),
    };
}

export const OrganisationSelectModalContainer = connect(mapStateToProps, {
    toggleUserOrganisationSelectModal,
    updateUserActiveOrganisation,
    redirectToUrl,
})(OrganisationSelectModal);
